import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AuthHeaderService } from 'src/app/services/auth-header.service';
import { SanctumService } from 'src/app/services/sanctum.service';
import { environment } from 'src/environments/environment';

export type MediaUploadPurpose = 'slick_media' | 'slick_link_icon' | 'store_identity' | 'profile_photo';

interface PresignResponse {
  key: string;
  upload_url: string;
  headers: { [key: string]: string };
  public_url: string;
}

// Substitui o antigo upload direto via aws-amplify/storage (Cognito Identity
// Pool, sem equivalente no R2): o backend gera uma presigned PUT URL e o
// navegador sobe o arquivo direto pro R2 com um XHR (fetch não expõe
// progresso de upload).
@Injectable({ providedIn: 'root' })
export class MediaUploadService {

  constructor(
    private http: HttpClient,
    private authHeaderService: AuthHeaderService,
    private sanctum: SanctumService,
    private zone: NgZone
  ) { }

  private get baseUrl(): string {
    return `${environment.apiBaseUrl}/api`;
  }

  async uploadForStore(
    file: File,
    storeId: string,
    purpose: MediaUploadPurpose,
    onProgress?: (percent: number) => void
  ): Promise<{ url: string; key: string }> {
    const url = `${this.baseUrl}/admin/store/${storeId}/media/presign`;
    const presign = await this.requestPresign(url, file, purpose);
    const publicUrl = await this.putToR2(file, presign, onProgress);
    return { url: publicUrl, key: presign.key };
  }

  async uploadForUser(
    file: File,
    userId: string,
    purpose: MediaUploadPurpose,
    onProgress?: (percent: number) => void
  ): Promise<{ url: string; key: string }> {
    const url = `${this.baseUrl}/users/${userId}/media/presign`;
    const presign = await this.requestPresign(url, file, purpose);
    const publicUrl = await this.putToR2(file, presign, onProgress);
    return { url: publicUrl, key: presign.key };
  }

  private async requestPresign(url: string, file: File, purpose: MediaUploadPurpose): Promise<PresignResponse> {
    await this.sanctum.initSanctum();
    const httpOptions = await this.authHeaderService.getHttpOptions();
    const body = {
      purpose,
      extension: this.getExt(file),
      content_type: file.type || 'application/octet-stream',
    };
    return await this.http.post<PresignResponse>(url, body, httpOptions).toPromise() as PresignResponse;
  }

  private putToR2(file: File, presign: PresignResponse, onProgress?: (percent: number) => void): Promise<string> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', presign.upload_url, true);

      Object.entries(presign.headers || {}).forEach(([name, value]) => {
        xhr.setRequestHeader(name, value);
      });

      // xhr.upload é um EventTarget separado do XHR principal — o zone.js
      // não garante o patch dele, então o progresso pode disparar fora da
      // zone do Angular e a view nunca re-renderiza sem isso.
      if (onProgress) {
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percent = Math.round((event.loaded / event.total) * 100);
            this.zone.run(() => onProgress(percent));
          }
        };
      }

      xhr.onload = () => {
        this.zone.run(() => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(presign.public_url);
          } else {
            reject(new Error(`Falha no upload pro R2 (status ${xhr.status}).`));
          }
        });
      };
      xhr.onerror = () => this.zone.run(() => reject(new Error('Falha de rede durante o upload.')));
      xhr.send(file);
    });
  }

  private getExt(file: File): string {
    const nameExt = file.name.split('.').pop()?.toLowerCase();
    if (nameExt) return nameExt;
    if (file.type === 'image/jpeg') return 'jpeg';
    if (file.type === 'image/png') return 'png';
    if (file.type === 'image/webp') return 'webp';
    if (file.type === 'image/avif') return 'avif';
    if (file.type === 'video/mp4') return 'mp4';
    return 'jpeg';
  }
}
